import React, {
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import {
  ASHLEY_HOME_CATALOG,
  getAshleyHomeCategoryById,
} from "./ashleyHomeCatalog";
import { getAshleyCapabilityStatusMeta } from "./ashleyCapabilityStatus";
import {
  excludeGatedAshleyHomeCategories,
  filterAshleyHomeCatalog,
} from "./filterAshleyHomeCatalog";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { AshleyHomeDesktop } from "./AshleyHomeDesktop";
import "./AshleyHomeScreen.css";

/**
 * Ashley Home — discovery operating center for Ashley capabilities.
 *
 * Architecture notes for later:
 * - Catalog is currently hand-authored but shaped like registry metadata.
 * - `expandedCategoryId` can be driven by voice ("Email") without redesign.
 * - Example phrases are informational only in V1 (no execute-on-tap).
 *
 * @param {{ onBack?: () => void, initialExpandedCategoryId?: string | null }} props
 */
export function AshleyHomeScreen({
  onBack,
  initialExpandedCategoryId = null,
}) {
  const isDesktop = useIsDesktopViewport();
  const searchId = useId();
  const categoriesId = useId();
  const [searchText, setSearchText] = useState("");
  const deferredSearchText = useDeferredValue(searchText);
  const [expandedCategoryId, setExpandedCategoryId] = useState(
    initialExpandedCategoryId,
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!initialExpandedCategoryId) {
      return;
    }
    if (getAshleyHomeCategoryById(initialExpandedCategoryId)) {
      setExpandedCategoryId(initialExpandedCategoryId);
    }
  }, [initialExpandedCategoryId]);

  const visibleCatalog = useMemo(
    () =>
      filterAshleyHomeCatalog(
        excludeGatedAshleyHomeCategories(ASHLEY_HOME_CATALOG),
        deferredSearchText,
      ),
    [deferredSearchText],
  );

  useEffect(() => {
    if (!deferredSearchText.trim()) {
      return;
    }

    setExpandedCategoryId((current) => {
      if (visibleCatalog.length === 1) {
        return visibleCatalog[0].id;
      }
      if (current && visibleCatalog.some((category) => category.id === current)) {
        return current;
      }
      return null;
    });
  }, [deferredSearchText, visibleCatalog]);

  const toggleCategory = (categoryId) => {
    setExpandedCategoryId((current) =>
      current === categoryId ? null : categoryId,
    );
  };

  if (isDesktop) {
    return (
      <AshleyHomeDesktop
        onBack={onBack}
        searchText={searchText}
        onSearch={setSearchText}
        catalog={visibleCatalog}
        selectedId={expandedCategoryId}
        onSelect={setExpandedCategoryId}
      />
    );
  }

  return (
    <div className="screen nx-screen ashley-home">
      <div className="ashley-home__atmosphere" aria-hidden="true" />

      <header className="ashley-home__header">
        {onBack ? (
          <button type="button" className="cockpit-back-btn" onClick={onBack}>
            Back
          </button>
        ) : null}

        <div className="ashley-home__intro">
          <h1 className="ashley-home__title">Ashley</h1>
          <p className="ashley-home__subtitle" id={`${searchId}-subtitle`}>
            What can I help you with today?
          </p>
          <p className="ashley-home__helper">
            Browse my capabilities or simply ask me naturally.
          </p>
        </div>

        <div className="ashley-home__search" role="search">
          <label className="ashley-home__search-label" htmlFor={searchId}>
            Search Ashley&apos;s capabilities
          </label>
          <input
            id={searchId}
            type="search"
            className="ashley-home__search-input"
            placeholder="Search Ashley's capabilities..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            autoComplete="off"
            enterKeyHint="search"
            aria-describedby={`${searchId}-subtitle`}
            aria-controls={categoriesId}
          />
        </div>
      </header>

      <div
        id={categoriesId}
        className="ashley-home__categories"
        role="list"
        aria-live="polite"
      >
        {visibleCatalog.length === 0 ? (
          <p className="ashley-home__empty" role="status">
            Nothing matches that search. Try email, payroll, briefing, or
            session.
          </p>
        ) : (
          visibleCatalog.map((category) => {
            const expanded = expandedCategoryId === category.id;
            const panelId = `ashley-home-panel-${category.id}`;
            const headerId = `ashley-home-header-${category.id}`;
            const capabilityCountLabel = `${category.capabilities.length} ${
              category.capabilities.length === 1 ? "capability" : "capabilities"
            }`;

            return (
              <section
                key={category.id}
                className={`ashley-home__category${
                  expanded ? " ashley-home__category--expanded" : ""
                }`}
                role="listitem"
              >
                <h2 className="ashley-home__category-heading">
                  <button
                    type="button"
                    id={headerId}
                    className="ashley-home__category-toggle"
                    aria-expanded={expanded}
                    aria-controls={panelId}
                    onClick={() => toggleCategory(category.id)}
                  >
                    <span className="ashley-home__category-icon" aria-hidden="true">
                      {category.icon}
                    </span>
                    <span className="ashley-home__category-title">
                      {category.title}
                    </span>
                    <span
                      className="ashley-home__category-meta"
                      aria-label={capabilityCountLabel}
                    >
                      {category.capabilities.length}
                    </span>
                    <span
                      className="ashley-home__category-chevron"
                      aria-hidden="true"
                    />
                  </button>
                </h2>

                <div
                  id={panelId}
                  className="ashley-home__panel"
                  role="region"
                  aria-labelledby={headerId}
                  hidden={!expanded}
                >
                  <div className="ashley-home__panel-section">
                    <h3 className="ashley-home__panel-label">
                      What Ashley can do
                    </h3>
                    <ul className="ashley-home__capability-list">
                      {category.capabilities.map((capability) => {
                        const statusMeta = getAshleyCapabilityStatusMeta(
                          capability.status,
                        );
                        return (
                          <li
                            key={capability.id}
                            className="ashley-home__capability"
                          >
                            <div className="ashley-home__capability-row">
                              <span
                                className={`ashley-home__status ashley-home__status--${statusMeta.indicator}`}
                                title={statusMeta.label}
                                aria-label={statusMeta.label}
                              />
                              <div className="ashley-home__capability-copy">
                                <p className="ashley-home__capability-title">
                                  {capability.title}
                                </p>
                                {capability.description ? (
                                  <p className="ashley-home__capability-description">
                                    {capability.description}
                                  </p>
                                ) : null}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <div className="ashley-home__panel-section">
                    <h3 className="ashley-home__panel-label">
                      Example phrases
                    </h3>
                    <p className="ashley-home__phrases-note">
                      Say it naturally — exact wording isn&apos;t required.
                    </p>
                    <ul className="ashley-home__phrase-list">
                      {category.capabilities.flatMap((capability) =>
                        (capability.examplePhrases || []).map((phrase) => (
                          <li
                            key={`${capability.id}:${phrase}`}
                            className="ashley-home__phrase"
                          >
                            {phrase}
                          </li>
                        )),
                      )}
                    </ul>
                  </div>
                </div>
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}
